import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { colors, CommonModal, OtpInput, PasswordSetup, TextFieldComponent } from '../../components';
import ButtonComponent from '../../components/button/ButtonComponent';
import { ContactSupportModalContent } from '../../components/contact-support/ContactSupportModalContent';
import CommonDatePicker, { DatePickerValidationReason } from '../../components/date-picker/CommonDatePicker';
import { CommonLoginWrapper, LinkTextWrapper } from '../../components/global-styles/GlobalStyles';
import { PhoneInputComponent } from '../../components/text-field/PhoneInputComponent';
import { BrandLogo } from '../../components/ui-components/BrandLogo';
import {
    businessNumberValidation,
    federalTaxIdValidation,
    firstNameOrLastNameValidation,
    formatBusinessNumberInput,
    formatFederalTaxIdInput,
    formatSINInput,
    formatSSNInput,
    sanitizeNameInput,
    sinNumberValidation,
    ssnNumberValidation,
} from '../../components/validations/validations';
import {
    registrationSecurityCheck,
    resendOtp,
    setUserPassword,
    signUpUser,
    submitNewUserDetails,
    verifyRegistrationOtp,
} from '../../services/registration-service';
import { RootState } from '../../store';
import { hideLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { invalidDatePickerUserMessage } from '../../utils/date-picker-invalid-date';
import {
    trackRegistrationCompleted,
    trackRegistrationFailed,
    trackRegistrationOtpFailed,
    trackRegistrationOtpVerified,
    trackRegistrationStarted,
} from '../../utils/google-analytics';
import {
    defaultTimerCount,
    getDialCodeForApi,
    getMaskedEmailForProp,
    isValidContractId,
    isValidPostalCode,
    isValidSSNNumber,
    regex,
} from '../../utils/helper';
import { clearMigrationCookiesForCurrentHost } from '../../utils/migration-cookies';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';

import {
    BrandLogoStyle,
    DescriptionText,
    EmailFieldWrapper,
    FieldWrapper,
    FooterLinksContainer,
    FooterLinkText,
    FooterTypography,
    FormFieldsContainer,
    FullWidthFieldWrapper,
    LoginCardStack,
    NavyActionLinkText,
    NavyLinkColor,
    LogoContainer,
    SeparatorText,
    StyledDialog,
    SupportLinksStack,
    SupportText,
    VerificationTitle,
} from './registration-styles';
import {
    ResendOtpRequest,
    ResendOtpResponse,
    SetUserPasswordPayload,
    SetUserPasswordResponse,
    SignUpRequest,
    SignUpResponse,
    VerifyRegistrationOtpRequest,
    VerifyRegistrationOtpResponse,
} from './registration-types';

type FormErrors = Partial<Record<string, string>>;

interface SecurityCheckPayload {
    ssn: string;
    dob?: string;
    postalCode?: string;
    portalId: number;
    id: number | undefined;
}

interface SecurityCheckData {
    accountType?: 'B' | 'I';
    details?: boolean;
    fname?: string;
    lname?: string;
    phonenumber?: string;
    dateofbirth?: string;
}

interface SecurityCheckResponse {
    statusCode: number;
    message: string;
    data: SecurityCheckData;
}

interface NewUserDetailsPayload {
    firstName: string;
    lastName: string;
    dateOfBirth?: string;
    phoneNumber: string;
    phoneExtension: string;
    id: number | undefined;
}

/** DOB must be at least 18 years ago and at most 120 years ago (rejects invalid years e.g. 0200). */
const DOB_MIN_DATE = dayjs().subtract(120, 'year').startOf('day');
const DOB_MAX_DATE = dayjs().subtract(18, 'year').startOf('day');

const isDobWithinAllowedRange = (value: Dayjs | null): boolean => {
    if (!value) return false;
    const d = dayjs(value);
    if (!d.isValid()) return false;
    const start = d.startOf('day');
    return !start.isBefore(DOB_MIN_DATE) && !start.isAfter(DOB_MAX_DATE);
};

/** Non-empty DOB: invalid calendar date or outside 18–120 year window. */
const getDobRangeErrorMessage = (value: Dayjs, t: (key: string) => string): string | undefined => {
    const d = dayjs(value);
    if (!d.isValid()) return t('datePicker.dateOfBirth.invalid');
    const start = d.startOf('day');
    if (start.isBefore(DOB_MIN_DATE)) return t('datePicker.dateOfBirth.maxAge120');
    if (start.isAfter(DOB_MAX_DATE)) return t('datePicker.dateOfBirth.minAge18');
    return undefined;
};

const mapPickerReasonToDobMessage = (
    reason: DatePickerValidationReason,
    t: (key: string) => string,
    meta?: { rawInput?: string },
): string => {
    if (reason === 'invalidDate') {
        return invalidDatePickerUserMessage(meta?.rawInput ?? '', 'dateOfBirth', t);
    }
    if (reason === 'disableFuture') return t('datePicker.dateOfBirth.future');
    if (reason === 'minDate') return t('datePicker.dateOfBirth.maxAge120');
    if (reason === 'maxDate') return t('datePicker.dateOfBirth.minAge18');
    if (reason === 'disablePast') return t('datePicker.dateOfBirth.invalid');
    return t('datePicker.dateOfBirth.invalid');
};

const isPhoneValidForNewUserDetails = (value: string): boolean => {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.length === 10;
};

const isMigrationRequiredResponse = (response: unknown): boolean => {
    if (response == null || typeof response !== 'object') return false;
    const r = response as Record<string, unknown>;
    const data = typeof r.data === 'object' && r.data != null ? (r.data as Record<string, unknown>) : {};

    // If user already exists with an account, this is NOT a migration-required case
    // (even if isMigrated flag is true). It's a "redirect to login" case instead.
    if (data.isExistingUser === true) {
        return false;
    }

    const topLevelFlag = r.isMigrate === true || r.isMigrated === true;
    const dataFlag = data.isMigrate === true || data.isMigrated === true;
    const message = typeof r.message === 'string' ? r.message.toLowerCase() : '';
    const messageSaysMigrate =
        message.includes('complete your migration') ||
        message.includes('migration process') ||
        message.includes('please complete your migration');
    return topLevelFlag || dataFlag || messageSaysMigrate;
};

export const SignUp: React.FC = () => {
    const [contractId, setContractId] = useState('');
    const [email, setEmail] = useState('');
    const [currentStep, setCurrentStep] = useState<
        'register' | 'securityCheck' | 'newUserDetailsForm' | 'verify' | 'password' | 'success'
    >('register');
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [errors, setErrors] = useState<FormErrors | undefined>();
    const [timerCount, setTimerCount] = useState(defaultTimerCount);
    const dispatch = useDispatch();
    const [isBusinessUser, setIsBusinessUser] = useState(false);
    const [countryName, setCountryName] = useState('');
    const [ssnNumber, setSSNNumber] = useState('');
    const [sinNumber, setSINNumber] = useState('');
    const [userDob, setUserDOB] = useState<Dayjs | null>(null);
    const [registrationId, setRegistrationId] = useState<number>();
    const [fedTaxNumber, setFedTaxNumber] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [businessNumber, setBusinessNumber] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [countryCode, setCountryCode] = useState('+1');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [userRegistrationDob, setUserRegistrationDOB] = useState<Dayjs | null>(null);
    const userDobTouchedRef = useRef(false);
    const userRegistrationDobTouchedRef = useRef(false);
    const userDobRef = useRef<Dayjs | null>(null);
    const userRegistrationDobRef = useRef<Dayjs | null>(null);
    userDobRef.current = userDob;
    userRegistrationDobRef.current = userRegistrationDob;
    const [openContactSupportModal, setOpenContactSupportModal] = useState(false);
    const brandDetails = useSelector((state: RootState) => state.brand.brandDetails);
    const isBrandLoading = useSelector((state: RootState) => state.brand.isBrandLoading);
    const resolvedPortalId = (() => {
        const id = Number(brandDetails?.Id);
        return Number.isFinite(id) && id > 0 ? id : 2;
    })();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // TODO: Prefill email and show toast when redirected from migration page (email not found case).
    useEffect(() => {
        const state = location.state as { prefillEmail?: string; toastMessage?: string; toastSeverity?: string } | null;
        if (state?.prefillEmail) {
            setEmail(state.prefillEmail.trim());
        }
        if (state?.toastMessage) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: state.toastMessage,
                    severity: state.toastSeverity || 'info',
                }),
            );
        }
    }, [location.state, dispatch]);

    // Helper function to strip formatting from SSN, SIN, Fed Tax, and Business Number
    // Removes all non-digit characters to send unformatted values to API
    const stripFormatting = (value: string): string => {
        return value.replace(/\D/g, '');
    };

    // Shared "set or clear a keyed form error" idiom, reused by the contract id, SSN/SIN/Fed
    // Tax/Business Number, postal/zip code, first/last name, and phone number field handlers below.
    const setFieldError = (key: string, message?: string) => {
        if (message) {
            setErrors(prevErrors => ({ ...prevErrors, [key]: message }));
        } else {
            setErrors(prevErrors => {
                if (prevErrors && prevErrors[key]) {
                    const rest = { ...prevErrors };
                    delete rest[key];
                    return rest;
                }
                return prevErrors || {};
            });
        }
    };

    const isValidForm = () => {
        const error: FormErrors = {};
        if (!email.trim()) {
            error.email = t('registration.errors.emailRequired');
        }
        if (email && !regex.emailRegExp.test(email)) {
            error.email = t('registration.errors.emailInvalid');
        }
        if (!contractId.trim()) {
            error.contractId = t('registration.errors.contractIdRequired');
        }
        if (contractId && !regex.contractIdRegExp.test(contractId)) {
            error.contractId = t('registration.errors.contractIdInvalid');
        }
        setErrors(error);
        return Object.keys(error).length === 0;
    };
    const handleRegistrationSubmit = async () => {
        if (isValidForm()) {
            try {
                const signUpPayload: SignUpRequest = {
                    contractId: contractId.trim(),
                    email: email.trim(),
                    portalId: resolvedPortalId,
                };
                const response: SignUpResponse = await signUpUser(signUpPayload, true);
                if (isMigrationRequiredResponse(response)) {
                    // Case A: Valid + Migration Required
                    // Backend is source of truth: stale migration cookies would send users from /update_portal → /login.
                    clearMigrationCookiesForCurrentHost();
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: t('registration.messages.migrationRequiredBeforeRegistration'),
                            severity: 'info',
                        }),
                    );
                    const prefillEmail = email.trim();
                    navigate(`${NavigationConfig.UpgradeAnnouncement}?source=update_portal`, {
                        state: { fromUpdatePortal: true, prefillEmail, forceMigrationFromRegistration: true },
                        replace: true,
                    });
                    return;
                }
                // Case B: Valid + Migration Already Done OR User Already Exists
                if (
                    response?.data?.isExistingUser === true ||
                    (response?.statusCode === 400 && response?.data?.isMigrated === true) ||
                    (response?.statusCode === 400 && response?.message?.toLowerCase().includes('already exists'))
                ) {
                    navigate(NavigationConfig.Login, {
                        state: {
                            fromMigration: true,
                            prefillEmail: email.trim(),
                            toastMessage: t('registration.messages.existingUserMessage'),
                            toastSeverity: 'info',
                        },
                        replace: true,
                    });
                    return;
                }
                if (response.statusCode === 200) {
                    // Case C: Valid + Migration Not Required + Email Doesn't exist
                    setIsBusinessUser(response.data.accountType === 'B');
                    setCountryName(response.data.country || 'USA');
                    setRegistrationId(response.data.id);
                    setCurrentStep('securityCheck');
                } else {
                    // Case D: Invalid Details
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: response.message,
                            severity: 'error',
                        }),
                    );
                }
            } catch (err) {
                const apiErr = err as { message?: string };
                const message =
                    apiErr?.message || (err instanceof Error ? err.message : 'Something went wrong. Please try again.');
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message,
                        severity: 'error',
                    }),
                );
            } finally {
                dispatch(hideLoader());
            }
        }
    };

    const isValidSecurityCheckUSAForm = () => {
        const error: FormErrors = {};
        if (!isBusinessUser) {
            if (!ssnNumber.trim()) {
                error.ssnNumber = t('registration.errors.ssnRequired');
            } else {
                const ssnError = ssnNumberValidation(ssnNumber);
                if (ssnError) {
                    error.ssnNumber = ssnError;
                }
            }
            if (!userDob) {
                error.userDob = t('datePicker.dateOfBirth.required');
            } else {
                const dobErr = getDobRangeErrorMessage(userDob, t);
                if (dobErr) error.userDob = dobErr;
            }
            if (!error.userDob && errors?.userDob) {
                error.userDob = errors.userDob;
            }
        } else {
            if (!fedTaxNumber.trim()) {
                error.fedTaxNumber = t('registration.errors.fedTaxRequired');
            } else {
                const taxError = federalTaxIdValidation(fedTaxNumber);
                if (taxError) {
                    error.fedTaxNumber = taxError;
                }
            }
            if (!zipCode.trim()) {
                error.zipCode = t('registration.errors.zipCodeRequired');
            } else if (/\s{2,}/.test(zipCode.trim())) {
                error.zipCode =
                    t('registration.errors.zipCodeMultipleSpaces') ||
                    'Zip code cannot contain multiple consecutive spaces.';
            } else if (!isValidPostalCode(zipCode)) {
                error.zipCode = t('registration.errors.zipCodeFormat');
            }
        }

        setErrors(error);

        return Object.keys(error).length === 0;
    };

    const isValidSecurityCheckNonUSAForm = () => {
        const error: FormErrors = {};
        if (!isBusinessUser) {
            if (!userDob) {
                error.userDob = t('datePicker.dateOfBirth.required');
            } else {
                const dobErr = getDobRangeErrorMessage(userDob, t);
                if (dobErr) error.userDob = dobErr;
            }
            if (!error.userDob && errors?.userDob) {
                error.userDob = errors.userDob;
            }
            if (!sinNumber.trim()) {
                error.sinNumber = t('registration.errors.sinRequired');
            } else {
                const sinError = sinNumberValidation(sinNumber);
                if (sinError) {
                    error.sinNumber = sinError;
                }
            }
        } else {
            if (!postalCode.trim()) {
                error.postalCode = t('registration.errors.postalCodeRequired');
            } else if (/\s{2,}/.test(postalCode.trim())) {
                error.postalCode =
                    t('registration.errors.postalCodeMultipleSpaces') ||
                    'Postal code cannot contain multiple consecutive spaces.';
            } else if (!isValidPostalCode(postalCode)) {
                error.postalCode = t('registration.errors.postalCodeFormat');
            }
            if (!businessNumber.trim()) {
                error.businessNumber = t('registration.errors.businessNumberRequired');
            } else {
                const bnError = businessNumberValidation(businessNumber);
                if (bnError) {
                    error.businessNumber = bnError;
                }
            }
        }
        setErrors(error);
        return Object.keys(error).length === 0;
    };
    // Shared request/response handling for the four security-check variants below (USA/non-USA x
    // personal/business). They only differ in which fields are sent, and the USA-personal variant
    // additionally seeds the new-user-details form from the response on success.
    const submitSecurityCheck = async (
        securityCheckPayload: SecurityCheckPayload,
        onDetailsFound?: (response: SecurityCheckResponse) => void,
    ) => {
        try {
            const response = (await registrationSecurityCheck(
                securityCheckPayload,
            )) as unknown as SecurityCheckResponse;
            updateUserTypeFromSecurityResponse(response);
            if (response.statusCode === 200 && response?.data?.details) {
                trackRegistrationStarted();
                setCurrentStep('newUserDetailsForm');
                onDetailsFound?.(response);
            } else if (response.statusCode === 200 && !response?.data?.details) {
                trackRegistrationStarted();
                setShowOtpModal(true);
            } else {
                trackRegistrationFailed('security_check', response.message);
                dispatch(openToast({ show: true, isAlertOpen: true, message: response.message, severity: 'error' }));
            }
        } catch (err: unknown) {
            const apiErr = err as { message?: string };
            const errMessage = apiErr?.message || (err instanceof Error ? err.message : String(err));
            trackRegistrationFailed('security_check', errMessage);
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: errMessage,
                    severity: 'error',
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleSecurityCheckSubmit = async () => {
        if (countryName === 'USA') {
            if (!isBusinessUser) {
                if (!isValidSecurityCheckUSAForm()) {
                    return;
                }
                const securityCheckPayload: SecurityCheckPayload = {
                    ssn: stripFormatting(ssnNumber),
                    dob: userDob?.format('YYYY-MM-DD'),
                    portalId: resolvedPortalId,
                    id: registrationId,
                };
                await submitSecurityCheck(securityCheckPayload, response => {
                    setFirstName(response.data.fname || '');
                    setLastName(response.data.lname || '');
                    setPhoneNumber(response.data.phonenumber || '');
                    if (response.data.dateofbirth) {
                        setUserRegistrationDOB(dayjs(response.data.dateofbirth, 'YYYY-MM-DD'));
                    }
                });
            } else {
                if (!isValidSecurityCheckUSAForm()) {
                    return;
                }
                const securityCheckPayload: SecurityCheckPayload = {
                    ssn: stripFormatting(fedTaxNumber),
                    postalCode: stripFormatting(zipCode),
                    portalId: resolvedPortalId,
                    id: registrationId,
                };
                await submitSecurityCheck(securityCheckPayload);
            }
        } else if (countryName !== 'USA') {
            if (!isBusinessUser) {
                if (!isValidSecurityCheckNonUSAForm()) {
                    return;
                }
                const securityCheckPayload: SecurityCheckPayload = {
                    ssn: stripFormatting(sinNumber),
                    dob: userDob?.format('YYYY-MM-DD'),
                    portalId: resolvedPortalId,
                    id: registrationId,
                };
                await submitSecurityCheck(securityCheckPayload);
            } else {
                if (!isValidSecurityCheckNonUSAForm()) {
                    return;
                }
                const securityCheckPayload: SecurityCheckPayload = {
                    ssn: stripFormatting(businessNumber),
                    postalCode,
                    portalId: resolvedPortalId,
                    id: registrationId,
                };
                await submitSecurityCheck(securityCheckPayload);
            }
        }
    };

    const isValidNewUserDetailsForm = () => {
        const error: FormErrors = {};

        // First Name validation (max 16 characters, alphanumeric)
        const firstNameError = firstNameOrLastNameValidation(firstName, 'First Name');
        if (firstNameError) error.firstName = firstNameError;

        // Last Name validation (max 16 characters, alphanumeric)
        const lastNameError = firstNameOrLastNameValidation(lastName, 'Last Name');
        if (lastNameError) error.lastName = lastNameError;

        // Phone Number validation
        if (!phoneNumber.trim()) {
            error.phoneNumber = t('registration.errors.phoneRequired');
        } else if (!isPhoneValidForNewUserDetails(phoneNumber)) {
            error.phoneNumber = t('registration.errors.phoneInvalid');
        }

        if (!isBusinessUser) {
            // Date of Birth validation - must be 18–120 years old
            if (!userRegistrationDob) {
                error.userRegistrationDob = t('datePicker.dateOfBirth.required');
            } else {
                const dobErr = getDobRangeErrorMessage(userRegistrationDob, t);
                if (dobErr) error.userRegistrationDob = dobErr;
            }

            // Typed date can fail DatePicker validation without updating parent state (CommonDatePicker skips onChange).
            if (!error.userRegistrationDob && errors?.userRegistrationDob) {
                error.userRegistrationDob = errors.userRegistrationDob;
            }
        }

        setErrors(error);
        return Object.keys(error).length === 0;
    };

    const handleNewUserDetailsFormSubmit = async () => {
        if (isValidNewUserDetailsForm()) {
            try {
                const newUserDetailsFormPayload: NewUserDetailsPayload = {
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    ...(isBusinessUser ? {} : { dateOfBirth: userRegistrationDob?.format('YYYY-MM-DD') }),
                    phoneNumber: phoneNumber.trim(),
                    phoneExtension: getDialCodeForApi(countryCode),
                    id: registrationId,
                };
                const response = await submitNewUserDetails(newUserDetailsFormPayload);
                if (response.statusCode === 200) {
                    setShowOtpModal(true);
                } else {
                    dispatch(
                        openToast({ show: true, isAlertOpen: true, message: response.message, severity: 'error' }),
                    );
                }
            } catch (err: unknown) {
                const apiErr = err as { message?: string };
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: apiErr?.message || (err instanceof Error ? err.message : String(err)),
                        severity: 'error',
                    }),
                );
            } finally {
                dispatch(hideLoader());
            }
        }
    };

    const handleResendOTP = async () => {
        setTimerCount(0);
        try {
            const resendOtpPayload: ResendOtpRequest = {
                // email: email,
                portalId: resolvedPortalId,
                id: registrationId,
            };
            const response: ResendOtpResponse = await resendOtp(resendOtpPayload, true);
            if (response.statusCode === 200) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message,
                        severity: 'success',
                    }),
                );
                setTimerCount(defaultTimerCount);
                setShowOtpModal(true);
            } else {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message,
                        severity: 'error',
                    }),
                );
            }
        } catch (err: unknown) {
            const apiErr = err as { message?: string };
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: apiErr?.message || (err instanceof Error ? err.message : String(err)),
                    severity: 'error',
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleVerifyOtp = async (otp: string) => {
        if (otp?.length === 6) {
            try {
                const verifyOtpPayload: VerifyRegistrationOtpRequest = {
                    otp: Number(otp),
                    id: registrationId,
                };
                const response: VerifyRegistrationOtpResponse = await verifyRegistrationOtp(verifyOtpPayload, true);
                if (response.statusCode === 200) {
                    trackRegistrationOtpVerified();
                    setShowOtpModal(false);
                    setCurrentStep('password');
                } else {
                    trackRegistrationOtpFailed(response.message);
                    dispatch(
                        openToast({
                            show: true,
                            isAlertOpen: true,
                            message: response.message,
                            severity: 'error',
                        }),
                    );
                }
            } catch (err: unknown) {
                const apiErr = err as { response?: { data?: { message?: string | string[] } }; message?: string };
                const raw = apiErr?.response?.data?.message || apiErr?.message;
                const message =
                    (Array.isArray(raw) ? raw[0] : raw) || t('registration.messages.invalidPasscodeMessage');
                trackRegistrationOtpFailed(message);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message,
                        severity: 'error',
                    }),
                );
            } finally {
                dispatch(hideLoader());
            }
        } else {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: t('registration.messages.invalidPasscodeMessage'),
                    severity: 'error',
                }),
            );
        }
    };

    const handleCloseOtpModal = () => {
        setShowOtpModal(false);
    };

    const handlePasswordSetupComplete = async (password: string) => {
        // call api to set password
        const setPasswordPayload: SetUserPasswordPayload = {
            password,
            id: registrationId,
        };
        try {
            const response: SetUserPasswordResponse = await setUserPassword(setPasswordPayload, true);
            if (response.statusCode === 200) {
                // Track successful registration
                trackRegistrationCompleted();

                setCurrentStep('success');
            } else {
                trackRegistrationFailed('password_setup', response.message);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message,
                        severity: 'error',
                    }),
                );
            }
        } catch (err: unknown) {
            const apiErr = err as { message?: string };
            const errMessage = apiErr?.message || (err instanceof Error ? err.message : String(err));
            trackRegistrationFailed('password_setup', errMessage);
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: errMessage,
                    severity: 'error',
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleGoToLogin = () => {
        navigate(`${NavigationConfig.Login}?source=login`, { state: { fromLogin: true }, replace: false });
    };

    const handleSupportClick = () => {
        setOpenContactSupportModal(true);
    };
    const handleLogin = () => {
        navigate(`${NavigationConfig.Login}?source=login`, { state: { fromLogin: true }, replace: false });
    };

    const handleSupportClose = () => {
        setOpenContactSupportModal(false);
    };

    // Keep verification DOB out of the flow for business users.
    useEffect(() => {
        if (!isBusinessUser) {
            return;
        }
        setUserRegistrationDOB(null);
        userRegistrationDobTouchedRef.current = false;
        setErrors(prev => {
            if (!prev?.userRegistrationDob) return prev;
            const rest = { ...prev };
            delete rest.userRegistrationDob;
            return rest;
        });
    }, [isBusinessUser]);

    const updateUserTypeFromSecurityResponse = (response: SecurityCheckResponse) => {
        const accountType = response?.data?.accountType;
        if (accountType === 'B') {
            setIsBusinessUser(true);
        } else if (accountType === 'I') {
            setIsBusinessUser(false);
        }
    };

    // Helper function to check if registration form is valid (for button disabled state)
    const isRegistrationFormValid = () => {
        return (
            email.trim() !== '' &&
            regex.emailRegExp.test(email) &&
            contractId.trim() !== '' &&
            isValidContractId(contractId)
        );
    };

    // Helper function to check if security check form is valid for non-business USA users
    const isSecurityCheckUSANonBusinessFormValid = () => {
        return (
            ssnNumber.trim() !== '' &&
            isValidSSNNumber(ssnNumber) &&
            isDobWithinAllowedRange(userDob) &&
            !errors?.userDob
        );
    };
    // Helper function to check if federal tax number is valid
    const isValidFederalTaxNumber = (): boolean => {
        if (!fedTaxNumber.trim()) {
            return false;
        }
        const error = federalTaxIdValidation(fedTaxNumber);
        return error === '';
    };

    // Helper function to check if security check form is valid for business USA users
    const isSecurityCheckUSABusinessFormValid = () => {
        return (
            fedTaxNumber.trim() !== '' &&
            isValidFederalTaxNumber() &&
            zipCode.trim() !== '' &&
            isValidPostalCode(zipCode)
        );
    };
    // Helper function to check if security check form is valid for non-business non-USA users
    const isSecurityCheckNonUSANonBusinessFormValid = () => {
        return (
            sinNumber.trim() !== '' &&
            isValidSSNNumber(sinNumber) &&
            isDobWithinAllowedRange(userDob) &&
            !errors?.userDob
        );
    };

    // Helper function to check if security check form is valid for business non-USA users
    const isSecurityCheckNonUSABusinessFormValid = () => {
        return (
            businessNumber.trim() !== '' &&
            isValidSSNNumber(businessNumber) &&
            postalCode.trim() !== '' &&
            isValidPostalCode(postalCode)
        );
    };

    // Helper function to check which security check form is valid based on country and user type
    const isSecurityCheckFormValid = () => {
        if (countryName === 'USA') {
            if (isBusinessUser) {
                return isSecurityCheckUSABusinessFormValid();
            } else {
                return isSecurityCheckUSANonBusinessFormValid();
            }
        } else {
            if (isBusinessUser) {
                return isSecurityCheckNonUSABusinessFormValid();
            } else {
                return isSecurityCheckNonUSANonBusinessFormValid();
            }
        }
    };

    // Helper function to check if new user details form is valid
    const isNewUserDetailsFormValid = () => {
        const isDobValidForCurrentUserType =
            isBusinessUser || (isDobWithinAllowedRange(userRegistrationDob) && !errors?.userRegistrationDob);

        return (
            !firstNameOrLastNameValidation(firstName, 'First Name') &&
            !firstNameOrLastNameValidation(lastName, 'Last Name') &&
            phoneNumber.trim() !== '' &&
            isPhoneValidForNewUserDetails(phoneNumber) &&
            isDobValidForCurrentUserType
        );
    };

    const handleContractIdChange = (value: string) => {
        setContractId(value);

        // Only validate if the user has entered something (user input allowed, then fire validation)
        if (value && !isValidContractId(value)) {
            setFieldError('contractId', t('registration.errors.contractIdFormat'));
        } else {
            setFieldError('contractId');
        }
    };

    const handleSSNNumberChange = (value: string, inputKey: string) => {
        let formattedValue = value;

        // Format based on field type
        if (inputKey === 'ssnNumber') {
            formattedValue = formatSSNInput(value);
            setSSNNumber(formattedValue);
        } else if (inputKey === 'sinNumber') {
            formattedValue = formatSINInput(value);
            setSINNumber(formattedValue);
        } else if (inputKey === 'fedTaxNumber') {
            formattedValue = formatFederalTaxIdInput(value);
            setFedTaxNumber(formattedValue);
        } else if (inputKey === 'businessNumber') {
            formattedValue = formatBusinessNumberInput(value);
            setBusinessNumber(formattedValue);
        }

        // Validate the input
        if (formattedValue) {
            let validationError = '';

            if (inputKey === 'ssnNumber') {
                validationError = ssnNumberValidation(formattedValue);
            } else if (inputKey === 'sinNumber') {
                validationError = sinNumberValidation(formattedValue);
            } else if (inputKey === 'fedTaxNumber') {
                validationError = federalTaxIdValidation(formattedValue);
            } else if (inputKey === 'businessNumber') {
                validationError = businessNumberValidation(formattedValue);
            }

            setFieldError(inputKey, validationError || undefined);
        } else {
            setFieldError(inputKey);
        }
    };

    const handlePostalCodeChange = (value: string, inputKey: string) => {
        if (inputKey === 'zipCode') {
            setZipCode(value);
        } else if (inputKey === 'postalCode') {
            setPostalCode(value);
        }

        // Validate postal/zip code
        const trimmedValue = value ? value.trim() : '';
        const minLength = 5;
        const maxLength = 9;
        let errorMsg = '';
        if (!trimmedValue) {
            // Clear error if field is empty (required validation will be handled on submit)
            setFieldError(inputKey);
            return;
        }
        // Check for multiple consecutive spaces
        if (/\s{2,}/.test(trimmedValue)) {
            errorMsg =
                inputKey === 'zipCode'
                    ? t('registration.errors.zipCodeMultipleSpaces') ||
                      'Zip code cannot contain multiple consecutive spaces.'
                    : t('registration.errors.postalCodeMultipleSpaces') ||
                      'Postal code cannot contain multiple consecutive spaces.';
        } else if (trimmedValue.length < minLength) {
            errorMsg =
                inputKey === 'zipCode'
                    ? t('registration.errors.zipCodeMinLength') || 'Zip code must be at least 5 characters.'
                    : t('registration.errors.postalCodeMinLength') || 'Postal code must be at least 5 characters.';
        } else if (trimmedValue.length > maxLength) {
            errorMsg =
                inputKey === 'zipCode'
                    ? t('registration.errors.zipCodeMaxLength') || 'Zip code must be at most 9 characters.'
                    : t('registration.errors.postalCodeMaxLength') || 'Postal code must be at most 9 characters.';
        } else if (!isValidPostalCode(trimmedValue)) {
            errorMsg =
                inputKey === 'zipCode'
                    ? t('registration.errors.zipCodeFormat')
                    : t('registration.errors.postalCodeFormat');
        }
        setFieldError(inputKey, errorMsg || undefined);
    };

    // Shared DOB change/picker-error handling, parameterized by which DOB field (the security-check
    // "userDob" vs the new-user-details "userRegistrationDob") is being edited. Both fields follow
    // the exact same touched/valid/error-message rules.
    const createDobChangeHandler =
        (
            touchedRef: React.MutableRefObject<boolean>,
            valueRef: React.MutableRefObject<Dayjs | null>,
            setValue: (value: Dayjs | null) => void,
            errorKey: 'userDob' | 'userRegistrationDob',
        ) =>
        (newValue: Dayjs | null) => {
            touchedRef.current = true;
            valueRef.current = newValue;
            setValue(newValue);
            if (!newValue) {
                setFieldError(errorKey, t('datePicker.dateOfBirth.required'));
                return;
            }
            const msg = getDobRangeErrorMessage(newValue, t);
            setFieldError(errorKey, msg);
        };

    const createDobPickerErrorHandler =
        (
            touchedRef: React.MutableRefObject<boolean>,
            valueRef: React.MutableRefObject<Dayjs | null>,
            errorKey: 'userDob' | 'userRegistrationDob',
        ) =>
        (reason: DatePickerValidationReason, pickerValue: Dayjs | null, meta?: { rawInput: string }) => {
            if (reason != null) {
                setFieldError(errorKey, mapPickerReasonToDobMessage(reason, t, meta));
                return;
            }
            const v = pickerValue ?? valueRef.current;
            if (!v) {
                setErrors(prev => {
                    if (!touchedRef.current) {
                        if (!prev?.[errorKey]) return prev;
                        const rest = { ...prev };
                        delete rest[errorKey];
                        return rest;
                    }
                    return { ...prev, [errorKey]: t('datePicker.dateOfBirth.required') };
                });
                return;
            }
            const msg = getDobRangeErrorMessage(v, t);
            setFieldError(errorKey, msg);
        };

    const handleDateOfBirthChange = createDobChangeHandler(userDobTouchedRef, userDobRef, setUserDOB, 'userDob');

    const handleUserDobPickerError = createDobPickerErrorHandler(userDobTouchedRef, userDobRef, 'userDob');

    const handleUserRegistrationDobChange = createDobChangeHandler(
        userRegistrationDobTouchedRef,
        userRegistrationDobRef,
        setUserRegistrationDOB,
        'userRegistrationDob',
    );

    const handleUserRegistrationDobPickerError = createDobPickerErrorHandler(
        userRegistrationDobTouchedRef,
        userRegistrationDobRef,
        'userRegistrationDob',
    );
    return (
        <CommonLoginWrapper role="main">
            {isBrandLoading ? (
                <Stack
                    className="loginCard"
                    sx={{
                        maxWidth: '458px !important',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 200,
                    }}
                >
                    <CircularProgress size={36} thickness={3} sx={{ color: 'primary.main' }} />
                </Stack>
            ) : (
                <>
                    {/* Main Content Card */}
                    <LoginCardStack className="loginCard" $currentStep={currentStep}>
                        {/* Logo Section */}
                        <LogoContainer>
                            <BrandLogo alt="Auxcap logo" style={BrandLogoStyle} />
                        </LogoContainer>
                        <Stack
                            className={
                                currentStep === 'register' ||
                                currentStep === 'password' ||
                                currentStep === 'securityCheck'
                                    ? 'commonLoginCard registerLoginCardBody'
                                    : 'commonLoginCard'
                            }
                        >
                            {currentStep === 'register' ? (
                                // Registration Form
                                <>
                                    {/* Register Title */}
                                    <Typography variant="h5" component="h1" className="mainTitle">
                                        {t('registration.titles.registration')}
                                    </Typography>
                                    <DescriptionText variant="body2" color="textPrimary">
                                        {t('registration.descriptions.registration')}{' '}
                                    </DescriptionText>
                                    {/* <HintText variant="body2" color="textSecondary">
                                        {t('registration.descriptions.contractIdHint1')}
                                    </HintText>
                                     <LastHintText variant="body2" color="textSecondary">
                                        {t('registration.descriptions.emailHint1')}
                                    </LastHintText>
                                    <HintText variant="body2" color="textSecondary">
                                        {t('registration.descriptions.emailHint2')}
                                    </HintText>
                                    <LastHintText variant="body2" color="textSecondary">
                                        {t('registration.descriptions.emailHint3')}
                                    </LastHintText> */}

                                    {/* Form Fields */}
                                    <FormFieldsContainer sx={{ mt: 5 }}>
                                        {/* Contract ID Field */}
                                        <FieldWrapper>
                                            <TextFieldComponent
                                                value={contractId}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    handleContractIdChange(e.target.value)
                                                }
                                                variant="outlined"
                                                error={errors?.contractId ? true : false}
                                                errorValue={errors?.contractId}
                                                type="password"
                                                maxLength={15}
                                                label={t('registration.labels.contractId')}
                                            />
                                        </FieldWrapper>

                                        {/* Email Field */}
                                        <EmailFieldWrapper>
                                            <TextFieldComponent
                                                type="email"
                                                value={email}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    setEmail(e.target.value)
                                                }
                                                variant="outlined"
                                                error={errors?.email ? true : false}
                                                errorValue={errors?.email}
                                                maxLength={50}
                                                label={t('registration.labels.emailAddress')}
                                            />
                                        </EmailFieldWrapper>
                                    </FormFieldsContainer>

                                    {/* Continue Button */}
                                    <ButtonComponent
                                        variant="contained"
                                        onClick={handleRegistrationSubmit}
                                        disabled={!isRegistrationFormValid()}
                                    >
                                        {t('registration.buttons.continue')}
                                    </ButtonComponent>
                                </>
                            ) : currentStep === 'password' ? (
                                // Password Setup Form
                                <PasswordSetup
                                    onPasswordSetupComplete={handlePasswordSetupComplete}
                                    onBack={() => setCurrentStep('register')}
                                />
                            ) : currentStep === 'securityCheck' && !isBusinessUser ? (
                                // Security Check Screen
                                // Registration Form
                                <>
                                    {/* Register Title */}
                                    <VerificationTitle variant="h5" component="h1" className="mainTitle">
                                        {t('registration.titles.verification')}
                                    </VerificationTitle>
                                    <Typography variant="body2" color="textPrimary" textAlign={'center'} mb={1.5}>
                                        {t('registration.descriptions.verificationPersonal')}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" textAlign={'center'} mb={3}>
                                        {t('registration.descriptions.verificationNote')}
                                    </Typography>
                                    {/* Form Fields */}
                                    <FormFieldsContainer>
                                        {/* SSN Field */}
                                        {countryName === 'USA' && (
                                            <FieldWrapper>
                                                <TextFieldComponent
                                                    value={ssnNumber}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleSSNNumberChange(e.target.value, 'ssnNumber')
                                                    }
                                                    variant="outlined"
                                                    error={errors?.ssnNumber ? true : false}
                                                    errorValue={errors?.ssnNumber}
                                                    type="password"
                                                    label={t('registration.labels.ssn')}
                                                    maxLength={11}
                                                    placeholder="XXX-XX-XXXX"
                                                />
                                            </FieldWrapper>
                                        )}

                                        {/* SIN Field */}
                                        {countryName !== 'USA' && (
                                            <FieldWrapper>
                                                <TextFieldComponent
                                                    value={sinNumber}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleSSNNumberChange(e.target.value, 'sinNumber')
                                                    }
                                                    variant="outlined"
                                                    type="password"
                                                    error={errors?.sinNumber ? true : false}
                                                    errorValue={errors?.sinNumber}
                                                    label={t('registration.labels.sin')}
                                                    maxLength={9}
                                                />
                                            </FieldWrapper>
                                        )}

                                        {/* DOB Field */}
                                        <FieldWrapper>
                                            <CommonDatePicker
                                                label={t('registration.labels.dateOfBirth')}
                                                value={userDob}
                                                onChange={handleDateOfBirthChange}
                                                onPickerError={handleUserDobPickerError}
                                                DatePickerErrorValue={errors?.userDob}
                                                error={Boolean(errors?.userDob)}
                                                minDate={DOB_MIN_DATE}
                                                maxDate={DOB_MAX_DATE}
                                                openTo="year"
                                                views={['year', 'month', 'day']}
                                                validationContext="dateOfBirth"
                                            />
                                        </FieldWrapper>
                                    </FormFieldsContainer>

                                    {/* Continue Button */}
                                    <ButtonComponent
                                        variant="contained"
                                        onClick={handleSecurityCheckSubmit}
                                        disabled={!isSecurityCheckFormValid()}
                                    >
                                        {t('registration.buttons.continue')}
                                    </ButtonComponent>
                                </>
                            ) : currentStep === 'securityCheck' && isBusinessUser ? (
                                // Security Check Screen
                                // Registration Form
                                <>
                                    {/* Register Title */}
                                    <Typography variant="h5" className="mainTitle">
                                        {t('registration.titles.verification')}
                                    </Typography>
                                    <DescriptionText variant="body2" color="textPrimary">
                                        {t('registration.descriptions.verificationBusiness')}
                                    </DescriptionText>
                                    {/* Form Fields */}
                                    <FormFieldsContainer>
                                        {/* SSN Field */}
                                        {countryName === 'USA' && (
                                            <>
                                                <FieldWrapper>
                                                    <TextFieldComponent
                                                        value={fedTaxNumber}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                            handleSSNNumberChange(e.target.value, 'fedTaxNumber')
                                                        }
                                                        variant="outlined"
                                                        error={errors?.fedTaxNumber ? true : false}
                                                        errorValue={errors?.fedTaxNumber}
                                                        type="password"
                                                        label={t('registration.labels.fedTaxNumber')}
                                                        maxLength={11}
                                                        placeholder="XX-XXXXXXX"
                                                    />
                                                </FieldWrapper>
                                                <Box>
                                                    <TextFieldComponent
                                                        value={zipCode}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                            handlePostalCodeChange(e.target.value, 'zipCode')
                                                        }
                                                        variant="outlined"
                                                        error={errors?.zipCode ? true : false}
                                                        errorValue={errors?.zipCode}
                                                        label={t('registration.labels.zipCode')}
                                                    />
                                                </Box>
                                            </>
                                        )}

                                        {/* SIN Field */}
                                        {countryName !== 'USA' && (
                                            <>
                                                <FieldWrapper>
                                                    <TextFieldComponent
                                                        value={businessNumber}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                            handleSSNNumberChange(e.target.value, 'businessNumber')
                                                        }
                                                        variant="outlined"
                                                        type="password"
                                                        error={errors?.businessNumber ? true : false}
                                                        errorValue={errors?.businessNumber}
                                                        label={t('registration.labels.businessNumber')}
                                                        maxLength={15}
                                                    />
                                                </FieldWrapper>
                                                <Box>
                                                    <TextFieldComponent
                                                        value={postalCode}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                            handlePostalCodeChange(e.target.value, 'postalCode')
                                                        }
                                                        variant="outlined"
                                                        error={errors?.postalCode ? true : false}
                                                        errorValue={errors?.postalCode}
                                                        label={t('registration.labels.postalCode')}
                                                        maxLength={9}
                                                    />
                                                </Box>
                                            </>
                                        )}

                                        {/* DOB Field */}
                                    </FormFieldsContainer>

                                    {/* Continue Button */}
                                    <ButtonComponent
                                        variant="contained"
                                        onClick={handleSecurityCheckSubmit}
                                        disabled={!isSecurityCheckFormValid()}
                                    >
                                        {t('registration.buttons.continue')}
                                    </ButtonComponent>
                                </>
                            ) : currentStep === 'newUserDetailsForm' ? (
                                // New User Details Form Screen
                                <>
                                    <VerificationTitle variant="h5" component="h1" className="mainTitle">
                                        {t('registration.titles.verification')}
                                    </VerificationTitle>

                                    {/* Example additional fields, please replace with correct ones as needed */}
                                    <FieldWrapper>
                                        <TextFieldComponent
                                            value={firstName}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const value = sanitizeNameInput(e.target.value);
                                                setFirstName(value);
                                                if (errors?.firstName) {
                                                    setFieldError('firstName');
                                                }
                                            }}
                                            variant="outlined"
                                            maxLength={16}
                                            error={errors?.firstName ? true : false}
                                            errorValue={errors?.firstName}
                                            label={t('registration.labels.firstName')}
                                            type="text"
                                        />
                                    </FieldWrapper>
                                    <FieldWrapper>
                                        <TextFieldComponent
                                            value={lastName}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const value = sanitizeNameInput(e.target.value);
                                                setLastName(value);
                                                if (errors?.lastName) {
                                                    setFieldError('lastName');
                                                }
                                            }}
                                            variant="outlined"
                                            maxLength={16}
                                            error={errors?.lastName ? true : false}
                                            errorValue={errors?.lastName}
                                            label={t('registration.labels.lastName')}
                                            type="text"
                                        />
                                    </FieldWrapper>
                                    {!isBusinessUser && (
                                        <FieldWrapper>
                                            {/* <TextFieldComponent
                                        value={registrationEmail}
                                        onChange={(e: any) => setRegistrationEmail(e.target.value)}
                                        variant="outlined"
                                        required
                                        error={errors?.registrationEmail ? true : false}
                                        errorValue={errors?.registrationEmail}
                                        label="Email"
                                    /> */}
                                            <CommonDatePicker
                                                label={t('registration.labels.dateOfBirth')}
                                                value={userRegistrationDob}
                                                onChange={handleUserRegistrationDobChange}
                                                onPickerError={handleUserRegistrationDobPickerError}
                                                DatePickerErrorValue={errors?.userRegistrationDob}
                                                error={Boolean(errors?.userRegistrationDob)}
                                                minDate={DOB_MIN_DATE}
                                                maxDate={DOB_MAX_DATE}
                                                openTo="year"
                                                views={['year', 'month', 'day']}
                                                validationContext="dateOfBirth"
                                            />
                                        </FieldWrapper>
                                    )}
                                    <FullWidthFieldWrapper>
                                        <PhoneInputComponent
                                            label={t('registration.labels.cellPhoneNumber')}
                                            countryCode={countryCode}
                                            onCountryChange={setCountryCode}
                                            value={phoneNumber}
                                            onChange={e => {
                                                setPhoneNumber(e.target.value);
                                                // Clear error when user starts typing
                                                if (errors?.phoneNumber) {
                                                    setFieldError('phoneNumber');
                                                }
                                            }}
                                            onBlur={() => {
                                                // Validate phone number on blur
                                                if (phoneNumber.trim()) {
                                                    if (!isPhoneValidForNewUserDetails(phoneNumber)) {
                                                        setErrors(prevErrors => ({
                                                            ...prevErrors,
                                                            phoneNumber: t('registration.errors.phoneInvalid'),
                                                        }));
                                                    }
                                                }
                                            }}
                                            error={errors?.phoneNumber ? true : false}
                                            errorValue={errors?.phoneNumber}
                                            maxLength={10}
                                        />
                                    </FullWidthFieldWrapper>
                                    <ButtonComponent
                                        variant="contained"
                                        onClick={handleNewUserDetailsFormSubmit}
                                        disabled={!isNewUserDetailsFormValid()}
                                    >
                                        {t('registration.buttons.continue')}
                                    </ButtonComponent>
                                </>
                            ) : (
                                // Success Screen
                                <>
                                    {/* Success Title */}
                                    <Typography textAlign={'center'} component="h1" variant="h5" className="mainTitle">
                                        {t('registration.titles.success')}
                                    </Typography>

                                    {/* Success Message */}
                                    <Typography
                                        variant="body2"
                                        color={colors.greyPreset[80]}
                                        mb={3}
                                        textAlign={'center'}
                                    >
                                        {t('registration.descriptions.successMessage')}
                                    </Typography>

                                    {/* Go to Login Button */}
                                    <ButtonComponent variant="contained" onClick={handleGoToLogin}>
                                        {t('registration.buttons.goToLogin')}
                                    </ButtonComponent>
                                </>
                            )}
                            {(currentStep === 'register' ||
                                currentStep === 'securityCheck' ||
                                currentStep === 'newUserDetailsForm') && (
                                <>
                                    {/* Support Link */}
                                    <SupportLinksStack>
                                        <SupportText variant="body2" whiteSpace={{ xs: 'wrap', md: 'nowrap' }}>
                                            {t('registration.links.troubleActivation')}{' '}
                                            <LinkTextWrapper
                                                onClick={handleSupportClick}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleSupportClick();
                                                    }
                                                }}
                                                tabIndex={0}
                                                role="button"
                                                sx={NavyActionLinkText}
                                            >
                                                {t('registration.links.contactSupport')}
                                            </LinkTextWrapper>
                                        </SupportText>
                                        <SupportText variant="body2" textAlign={'center'}>
                                            {t('registration.links.haveAccount')}{' '}
                                            <LinkTextWrapper
                                                onClick={handleLogin}
                                                sx={NavyActionLinkText}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleLogin();
                                                    }
                                                }}
                                                tabIndex={0}
                                                role="button"
                                            >
                                                {t('registration.links.login')}
                                            </LinkTextWrapper>
                                        </SupportText>
                                        <FooterLinksContainer>
                                            <FooterTypography variant="overline">
                                                <LinkTextWrapper
                                                    sx={theme => ({ ...FooterLinkText(theme), ...NavyLinkColor })}
                                                    target="_blank"
                                                    href="https://auxcap.com/terms-of-use/"
                                                >
                                                    {t('registration.links.termsOfUse')}
                                                </LinkTextWrapper>
                                                <SeparatorText variant="overline" color="textPrimary">
                                                    {' | '}
                                                </SeparatorText>
                                                <LinkTextWrapper
                                                    sx={theme => ({ ...FooterLinkText(theme), ...NavyLinkColor })}
                                                    target="_blank"
                                                    href="https://auxcap.com/privacy-policy/"
                                                >
                                                    {t('registration.links.privacyPolicy')}
                                                </LinkTextWrapper>
                                            </FooterTypography>
                                        </FooterLinksContainer>
                                    </SupportLinksStack>
                                    <CommonModal
                                        modalOpen={openContactSupportModal}
                                        onCloseModal={handleSupportClose}
                                        DialogMainTitle={t('registration.contactSupport.title')}
                                        showPrimaryButton={false}
                                        secondaryBtnName="Got It"
                                        secondaryBtnClick={handleSupportClose}
                                        ModalMaxWidth={400}
                                    >
                                        <ContactSupportModalContent />
                                    </CommonModal>
                                </>
                            )}
                        </Stack>
                    </LoginCardStack>

                    {/* Passcode modal */}
                    <StyledDialog
                        open={showOtpModal}
                        onClose={(_event, reason) => {
                            // Only allow closing via close button, not backdrop click or escape key
                            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                                return;
                            }
                            handleCloseOtpModal();
                        }}
                        disableEscapeKeyDown
                        aria-labelledby="otp-dialog-title"
                        aria-describedby="otp-dialog-description"
                    >
                        <OtpInput
                            mainTitle={t('registration.otp.title')}
                            email={email}
                            // update the subtitle like below to make it dynamic
                            subtitle={`${t('registration.otp.subtitle').replace('{{length}}', '6').replace('{{email}}', getMaskedEmailForProp(email))}`}
                            length={6}
                            onChange={() => {}}
                            onVerify={otp => handleVerifyOtp(otp)}
                            verifyButtonText={t('registration.buttons.verify')}
                            onClose={() => setShowOtpModal(false)}
                            onResend={() => {
                                handleResendOTP();
                            }}
                            timerCount={timerCount}
                            resendTextWithTimer={t('registration.otp.resend')}
                        />
                    </StyledDialog>
                </>
            )}
        </CommonLoginWrapper>
    );
};
